import { Link } from 'react-router-dom'
import LegalLayout from '../components/LegalLayout'
import SeoHead from '../components/SeoHead'
import { LEGAL } from '../lib/links'
import { openCookiePreferences } from '../lib/cookieConsent'
import { orgSchema, breadcrumbSchema } from '../lib/seo'
import { useT, useLocale } from '../i18n/LocaleContext'
import { localizedPath } from '../i18n/routes'

const UPDATED_AT = '3 août 2026'

export default function CookiePage() {
  const { t } = useT()
  const locale = useLocale()
  if (locale === 'en') return <CookiePageEn />
  if (locale === 'nl') return <CookiePageNl />
  return (
    <>
      <SeoHead
        title="Politique cookies — Yaslan"
        description="Politique d'utilisation des cookies sur yaslan.be. Quels cookies nous utilisons, à quelles fins, et comment gérer vos préférences."
        path="/cookies"
        jsonLd={[
          orgSchema(locale),
          breadcrumbSchema([
            { name: t('breadcrumb.home'), path: localizedPath('home', locale) },
            { name: t('breadcrumb.cookies'), path: localizedPath('cookies', locale) },
          ]),
        ]}
      />
      <LegalLayout
        breadcrumb="Cookies"
        badge="Légal · Cookies"
        title={
          <>
            Politique{' '}
            <span className="font-semibold bg-gradient-to-r from-accent-600 via-accent-500 to-accent-700 bg-clip-text text-transparent pb-[0.1em] inline-block">
              cookies
            </span>
            .
          </>
        }
        intro={
          <>
            Cette page explique quels cookies nous utilisons sur yaslan.be, à quelles fins, et
            comment vous pouvez gérer vos préférences à tout moment.
          </>
        }
        updatedAt={UPDATED_AT}
      >
        <h2>1. Qu'est-ce qu'un cookie&nbsp;?</h2>
        <p>
          Un cookie est un petit fichier texte déposé sur votre terminal (ordinateur, tablette,
          smartphone) lors de la visite d'un site web. Il permet au site de reconnaître votre
          navigateur, de mémoriser certaines informations et d'analyser votre navigation.
        </p>

        <h2>2. Cookies utilisés sur yaslan.be</h2>
        <p>
          Conformément au RGPD et à la directive ePrivacy, nous classons les cookies en deux
          catégories&nbsp;:
        </p>

        <h3>2.1. Cookies strictement nécessaires</h3>
        <p>
          Ces cookies sont indispensables au fonctionnement du site. Ils ne nécessitent pas votre
          consentement.
        </p>
        <ul>
          <li><strong>yaslan_consent</strong> — mémorise vos choix de consentement aux cookies (durée&nbsp;: 6 mois)</li>
        </ul>

        <h3>2.2. Cookies de mesure d'audience (optionnels)</h3>
        <p>
          Ces cookies nous permettent de comprendre comment les visiteurs utilisent notre site
          afin d'améliorer son contenu. Ils ne sont déposés qu'avec votre consentement explicite.
        </p>
        <ul>
          <li>Données collectées de manière anonymisée&nbsp;: pages visitées, durée de visite, origine du trafic, type d'appareil</li>
          <li>Aucun profilage publicitaire, aucune revente à des tiers</li>
        </ul>

        <h2>3. Pas de cookies publicitaires</h2>
        <p>
          Nous n'utilisons <strong>aucun cookie publicitaire</strong>, aucun pixel de réseau social et
          aucun outil de retargeting sur yaslan.be.
        </p>

        <h2>4. Gérer vos préférences</h2>
        <p>
          Vous pouvez à tout moment modifier vos choix de consentement en cliquant sur le bouton
          ci-dessous&nbsp;:
        </p>
        <p>
          <button
            type="button"
            onClick={openCookiePreferences}
            className="inline-flex items-center gap-2 rounded-full bg-accent-700 hover:bg-accent-800 text-white px-5 py-2.5 text-sm font-medium transition-colors no-underline"
          >
            Gérer mes préférences cookies
          </button>
        </p>
        <p>
          Vous pouvez également configurer votre navigateur pour bloquer les cookies. La plupart
          des navigateurs proposent cette option dans leurs paramètres de confidentialité&nbsp;:
        </p>
        <ul>
          <li><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer">Google Chrome</a></li>
          <li><a href="https://support.mozilla.org/kb/cookies-information-websites-store-on-your-computer" target="_blank" rel="noopener noreferrer">Mozilla Firefox</a></li>
          <li><a href="https://support.apple.com/fr-fr/guide/safari/sfri11471/mac" target="_blank" rel="noopener noreferrer">Safari</a></li>
          <li><a href="https://support.microsoft.com/help/4027947" target="_blank" rel="noopener noreferrer">Microsoft Edge</a></li>
        </ul>

        <h2>5. Responsable du traitement</h2>
        <p>
          {LEGAL.company} ({LEGAL.legalForm}), TVA {LEGAL.vat}. Pour toute question, voir notre{' '}
          <Link to="/privacy">Politique de confidentialité</Link>.
        </p>
      </LegalLayout>
    </>
  )
}

function CookiePageEn() {
  const { t } = useT()
  const locale = useLocale()
  return (
    <>
      <SeoHead
        title="Cookie Policy — Yaslan"
        description="Cookie policy for yaslan.be. Which cookies we use, for what purposes, and how to manage your preferences."
        path={localizedPath('cookies', locale)}
        jsonLd={[
          orgSchema(locale),
          breadcrumbSchema([
            { name: t('breadcrumb.home'), path: localizedPath('home', locale) },
            { name: t('breadcrumb.cookies'), path: localizedPath('cookies', locale) },
          ]),
        ]}
      />
      <LegalLayout
        breadcrumb="Cookies"
        badge="Legal · Cookies"
        title={
          <>
            Cookie{' '}
            <span className="font-semibold bg-gradient-to-r from-accent-600 via-accent-500 to-accent-700 bg-clip-text text-transparent pb-[0.1em] inline-block">
              Policy
            </span>
            .
          </>
        }
        intro={
          <>
            This page explains which cookies we use on yaslan.be, for what purposes, and how you
            can manage your preferences at any time.
          </>
        }
        updatedAt="3 August 2026"
      >
        <h2>1. What is a cookie?</h2>
        <p>
          A cookie is a small text file placed on your device (computer, tablet, smartphone) when
          you visit a website. It allows the site to recognise your browser, remember certain
          information, and analyse your browsing behaviour.
        </p>

        <h2>2. Cookies used on yaslan.be</h2>
        <p>
          In accordance with the GDPR and the ePrivacy Directive, we classify cookies into two
          categories:
        </p>

        <h3>2.1. Strictly necessary cookies</h3>
        <p>
          These cookies are essential for the website to function. They do not require your
          consent.
        </p>
        <ul>
          <li><strong>yaslan_consent</strong> — stores your cookie consent choices (retention period: 6 months)</li>
        </ul>

        <h3>2.2. Analytics cookies (optional)</h3>
        <p>
          These cookies help us understand how visitors use our site so we can improve its content.
          They are only placed with your explicit consent.
        </p>
        <ul>
          <li>Data collected in anonymised form: pages visited, visit duration, traffic source, device type</li>
          <li>No advertising profiling, no resale to third parties</li>
        </ul>

        <h2>3. No advertising cookies</h2>
        <p>
          We use <strong>no advertising cookies</strong>, no social-media pixels, and no
          retargeting tools on yaslan.be.
        </p>

        <h2>4. Managing your preferences</h2>
        <p>
          You can modify your consent choices at any time by clicking the button below:
        </p>
        <p>
          <button
            type="button"
            onClick={openCookiePreferences}
            className="inline-flex items-center gap-2 rounded-full bg-accent-700 hover:bg-accent-800 text-white px-5 py-2.5 text-sm font-medium transition-colors no-underline"
          >
            Manage my cookie preferences
          </button>
        </p>
        <p>
          You can also configure your browser to block cookies. Most browsers offer this option in
          their privacy settings:
        </p>
        <ul>
          <li><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer">Google Chrome</a></li>
          <li><a href="https://support.mozilla.org/kb/cookies-information-websites-store-on-your-computer" target="_blank" rel="noopener noreferrer">Mozilla Firefox</a></li>
          <li><a href="https://support.apple.com/fr-fr/guide/safari/sfri11471/mac" target="_blank" rel="noopener noreferrer">Safari</a></li>
          <li><a href="https://support.microsoft.com/help/4027947" target="_blank" rel="noopener noreferrer">Microsoft Edge</a></li>
        </ul>

        <h2>5. Data controller</h2>
        <p>
          {LEGAL.company} ({LEGAL.legalForm}), VAT {LEGAL.vat}. For any questions, see our{' '}
          <Link to={localizedPath('privacy', locale)}>Privacy Policy</Link>.
        </p>
      </LegalLayout>
    </>
  )
}

function CookiePageNl() {
  const { t } = useT()
  const locale = useLocale()
  return (
    <>
      <SeoHead
        title="Cookiebeleid — Yaslan"
        description="Cookiebeleid voor yaslan.be. Welke cookies wij gebruiken, voor welke doeleinden en hoe u uw voorkeuren kunt beheren."
        path={localizedPath('cookies', locale)}
        jsonLd={[
          orgSchema(locale),
          breadcrumbSchema([
            { name: t('breadcrumb.home'), path: localizedPath('home', locale) },
            { name: t('breadcrumb.cookies'), path: localizedPath('cookies', locale) },
          ]),
        ]}
      />
      <LegalLayout
        breadcrumb="Cookies"
        badge="Juridisch · Cookies"
        title={
          <>
            Cookie
            <span className="font-semibold bg-gradient-to-r from-accent-600 via-accent-500 to-accent-700 bg-clip-text text-transparent pb-[0.1em] inline-block">
              beleid
            </span>
            .
          </>
        }
        intro={
          <>
            Deze pagina legt uit welke cookies wij gebruiken op yaslan.be, voor welke doeleinden
            en hoe u uw voorkeuren op elk moment kunt beheren.
          </>
        }
        updatedAt="3 augustus 2026"
      >
        <h2>1. Wat is een cookie?</h2>
        <p>
          Een cookie is een klein tekstbestand dat op uw apparaat (computer, tablet, smartphone)
          wordt geplaatst wanneer u een website bezoekt. Het stelt de site in staat uw browser te
          herkennen, bepaalde informatie te onthouden en uw browsegedrag te analyseren.
        </p>

        <h2>2. Cookies gebruikt op yaslan.be</h2>
        <p>
          In overeenstemming met de AVG (GDPR) en de ePrivacy-richtlijn verdelen wij cookies in
          twee categorieën:
        </p>

        <h3>2.1. Strikt noodzakelijke cookies</h3>
        <p>
          Deze cookies zijn onmisbaar voor het functioneren van de website. Ze vereisen uw
          toestemming niet.
        </p>
        <ul>
          <li><strong>yaslan_consent</strong> — slaat uw cookietoestemmingskeuzes op (bewaartermijn: 6 maanden)</li>
        </ul>

        <h3>2.2. Analytische cookies (optioneel)</h3>
        <p>
          Deze cookies helpen ons te begrijpen hoe bezoekers onze site gebruiken, zodat wij de
          inhoud kunnen verbeteren. Ze worden alleen geplaatst met uw uitdrukkelijke toestemming.
        </p>
        <ul>
          <li>Gegevens geanonimiseerd verzameld: bezochte pagina's, bezoekduur, verkeersbron, apparaattype</li>
          <li>Geen reclameprofiling, geen doorverkoop aan derden</li>
        </ul>

        <h2>3. Geen advertentiecookies</h2>
        <p>
          Wij gebruiken <strong>geen advertentiecookies</strong>, geen sociale-mediapixels en geen
          retargetingtools op yaslan.be.
        </p>

        <h2>4. Uw voorkeuren beheren</h2>
        <p>
          U kunt uw toestemmingskeuzes op elk moment wijzigen door op de onderstaande knop te
          klikken:
        </p>
        <p>
          <button
            type="button"
            onClick={openCookiePreferences}
            className="inline-flex items-center gap-2 rounded-full bg-accent-700 hover:bg-accent-800 text-white px-5 py-2.5 text-sm font-medium transition-colors no-underline"
          >
            Mijn cookievoorkeuren beheren
          </button>
        </p>
        <p>
          U kunt uw browser ook instellen om cookies te blokkeren. De meeste browsers bieden deze
          optie in hun privacyinstellingen:
        </p>
        <ul>
          <li><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer">Google Chrome</a></li>
          <li><a href="https://support.mozilla.org/kb/cookies-information-websites-store-on-your-computer" target="_blank" rel="noopener noreferrer">Mozilla Firefox</a></li>
          <li><a href="https://support.apple.com/fr-fr/guide/safari/sfri11471/mac" target="_blank" rel="noopener noreferrer">Safari</a></li>
          <li><a href="https://support.microsoft.com/help/4027947" target="_blank" rel="noopener noreferrer">Microsoft Edge</a></li>
        </ul>

        <h2>5. Verwerkingsverantwoordelijke</h2>
        <p>
          {LEGAL.company} ({LEGAL.legalForm}), btw {LEGAL.vat}. Voor vragen, zie ons{' '}
          <Link to={localizedPath('privacy', locale)}>Privacybeleid</Link>.
        </p>
      </LegalLayout>
    </>
  )
}
